import React from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Box } from "@mui/material"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import { convertToLocalDateTimeWithSecs } from "../../../helpers/utils"
import LivisModal from "../../Modals/LivisModal"
import { useTranslation } from "react-i18next"

interface AgentMarkdownProps {
  text: string
  isStreaming?: boolean
}

export function AgentMarkdown({ text, isStreaming }: AgentMarkdownProps) {
  const { t } = useTranslation()
  if (!text) return null

  // Automatically localize any raw ISO timestamps found in the text (tables, summaries, etc)
  let processedText = text.replace(
    /\b(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)\b/g,
    (match, isoStr) => {
      try {
        const formatted = convertToLocalDateTimeWithSecs(isoStr)
        return formatted || match
      } catch {
        return match
      }
    }
  )

  // Hide incomplete markdown image tags at the end of the stream
  processedText = processedText.replace(
    /!\[[^\]]*\]\([^)]*$/,
    "\n*[🖼️ Loading Image...]*\n"
  )

  // Strip list numbers/bullets (like "1. " or "- ") if the line only contains an image
  processedText = processedText.replace(
    /^(?:\d+\.|[-*])\s*(!\[[^\]]*\]\([^)]*\))\s*$/gm,
    "$1"
  )

  // Collapse consecutive images separated by newlines onto the same line so they render side-by-side
  processedText = processedText.replace(
    /(!\[[^\]]*\]\([^)]*\))\n+(?=!\[)/g,
    "$1 "
  )

  const components = {
    img: ({ src, alt, ...props }: any) => {
      const [isFullscreen, setIsFullscreen] = React.useState(false)

      // Lower the AI chat wrapper z-index while the image modal is open so it
      // appears above the chat window (MUI Dialog default z-index is 1300).
      React.useEffect(() => {
        if (isFullscreen) {
          document.body.classList.add("livis-ai-modal-open")
        } else {
          document.body.classList.remove("livis-ai-modal-open")
        }
        return () => {
          document.body.classList.remove("livis-ai-modal-open")
        }
      }, [isFullscreen])

      return (
        <React.Fragment>
          <Box
            className="livis-ai-markdown-image-wrapper"
            sx={{
              position: "relative",
              display: "inline-block",
              cursor: "pointer",
              marginTop: "0.5em",
              marginRight: "8px",
              verticalAlign: "top",
              "&:hover .image-overlay": { opacity: 1 },
            }}
            onClick={() => setIsFullscreen(true)}
          >
            <img
              src={src}
              alt={alt}
              style={{
                maxWidth: "250px",
                maxHeight: "200px",
                borderRadius: "4px",
                display: "block",
                objectFit: "cover",
              }}
              {...props}
            />
            <Box
              className="image-overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "var(--livis-transparent-black-muted-bg-color)",
                opacity: 0,
                transition: "opacity 0.2s",
                display: isStreaming ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--livis-white-color)",
                borderRadius: "4px",
              }}
            >
              <FullscreenIcon sx={{ fontSize: 48 }} />
            </Box>
          </Box>

          <LivisModal
            size="large"
            modal_open={isFullscreen}
            livis_dismiss_btn={() => setIsFullscreen(false)}
            cancel_icon={true}
            title={alt || (t("Image Preview") as string)}
            animation_type="fade"
            disable_outside_click={false}
            bodyHeight="80vh"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                minHeight: "60vh",
                p: 2,
              }}
            >
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                  display: "block",
                }}
              />
            </Box>
          </LivisModal>
        </React.Fragment>
      )
    },
  }
  return (
    <Box
      className="agent-markdown"
      sx={{
        "& p": { margin: "0 0 0.5em" },
        "& p:last-child": { marginBottom: 0 },
        "& ul, & ol": { margin: "0.25em 0", paddingLeft: "1.25em" },
        "& pre": {
          overflow: "auto",
          fontSize: "0.85em",
          padding: "0.5em",
          borderRadius: 1,
          bgcolor: "action.hover",
        },
        "& code": { fontSize: "0.9em" },
        "& table": {
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "100%",
          fontSize: "0.85rem",
          backgroundColor: "var(--chat-surface, var(--livis-white-color))",
          borderRadius: "0.5rem",
          border: "1px solid var(--chat-border, var(--livis-border-color))",
          overflow: "hidden",
          my: 1,
        },
        "& th": {
          backgroundColor: "var(--livis-color-background, #f5f5fa)",
          fontWeight: 600,
          textAlign: "left",
          px: 1.5,
          py: 0.75,
          borderBottom:
            "1px solid var(--chat-border, var(--livis-border-color))",
        },
        "& td": {
          px: 1.5,
          py: 0.75,
          borderBottom:
            "1px solid var(--chat-border, var(--livis-border-color))",
        },
        "& tr:last-child td": { borderBottom: "none" },
        "& img": { maxWidth: "100%", height: "auto", borderRadius: "4px" },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components as any}>
        {processedText}
      </ReactMarkdown>
    </Box>
  )
}
